import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, DatePicker, TimePicker, Button, Space, Table, Card, Popconfirm, Row, Col, notification } from "antd";
import { PlusOutlined, MinusCircleOutlined, FileTextOutlined } from "@ant-design/icons";
import { useCustom, useCustomMutation } from "@refinedev/core";
import dayjs from "dayjs";
import { DEFAULT_COMMITTEE_LIST_QUERY } from "../../default-committee/queries";
import { MINUTE_TEMPLATE_LIST_QUERY } from "../../minute-template/queries";
import { CREATE_PROCUREMENT_MINUTE_MUTATION } from "../queries";

const { Option } = Select;
const { TextArea } = Input;

interface ICreateMinuteDialogProps {
  visible: boolean;
  onCancel: () => void;
  sourcingId: string;
  sourcingTitle: string;
  sourcingType: string;
  invitedSuppliers: any[];
  publishedDate?: string;
  openedAt?: string;
  submittedBids: any[];
  onSuccess: () => void;
}

export const CreateMinuteDialog: React.FC<ICreateMinuteDialogProps> = ({
  visible,
  onCancel,
  sourcingId,
  sourcingTitle,
  sourcingType,
  invitedSuppliers,
  publishedDate,
  openedAt,
  submittedBids,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [minuteType, setMinuteType] = useState<string>("");
  const [committeeMembers, setCommitteeMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Mutation for creating procurement minute
  const { mutate: createProcurementMinute, isLoading } = useCustomMutation();

  // Fetch default committees based on minute type
  const { data: committeeData, isLoading: committeeLoading } = useCustom({
    url: "",
    method: "get",
    meta: {
      gqlQuery: DEFAULT_COMMITTEE_LIST_QUERY,
      gqlVariables: {
        minuteType,
      },
    },
    queryOptions: {
      enabled: !!minuteType,
    },
  });

  // Fetch minute templates based on minute type
  const { data: templateData, isLoading: templateLoading } = useCustom({
    url: "",
    method: "get",
    meta: {
      gqlQuery: MINUTE_TEMPLATE_LIST_QUERY,
      gqlVariables: {
        minuteType,
      },
    },
    queryOptions: {
      enabled: !!minuteType,
    },
  });

  // Update committee members when minute type is selected
  useEffect(() => {
    if (committeeData?.data?.defaultCommittees?.nodes) {
      const activeMembers = committeeData.data.defaultCommittees.nodes
        .flatMap((committee: any) => 
          committee.members.filter((member: any) => member.isActive)
        )
        .map((member: any) => ({
          fullName: member.fullName,
          role: member.role,
          organization: member.organization,
          linkedUserId: member.linkedUserId,
        }));
      
      setCommitteeMembers(activeMembers);
      form.setFieldValue("committeeMembers", activeMembers);
    }
  }, [committeeData]);

  // Auto-populate venue when template is selected
  const handleTemplateChange = (templateId: string) => {
    const selectedTemplate = templateData?.data?.minuteTemplates?.nodes.find(
      (template: any) => template.id === templateId
    );
    if (selectedTemplate?.venue) {
      form.setFieldValue("venue", selectedTemplate.venue);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      // Debug: Log the raw values to see what's being passed
      console.log("Raw form values:", values);
      
      // Create a clean object with only the allowed top-level fields
      const formattedValues = {
        sourcingId,
        minuteType: values.minuteType,
        minuteTitle: values.minuteTitle,
        committeeMembers: values.committeeMembers,
        templateId: values.templateId,
        venue: values.venue,
        inputs: {
          minuteTitle: values.minuteTitle,
          sourcingTitle,
          sourcingType,
          committeeMembers: values.committeeMembers,
          bidOpenDate: values.bidOpenDate?.format("YYYY-MM-DD"),
          bidOpenTime: values.bidOpenTime?.format("HH:mm"),
          meetingStart: values.meetingStart?.format("YYYY-MM-DD HH:mm"),
          meetingEnd: values.meetingEnd?.format("YYYY-MM-DD HH:mm"),
          invitedSuppliers,
          publishedDate,
          submittedBids,
          venue: values.venue,
        },
      };

      console.log("Formatted values for mutation:", formattedValues);

      // Call the custom mutation
      createProcurementMinute({
        meta: {
          gqlMutation: CREATE_PROCUREMENT_MINUTE_MUTATION,
        },
        values: { input: formattedValues },
        url: "",
        method: "post"
      }, {
        onSuccess: () => {
          notification.success({
            message: "Minute Created",
            description: "The procurement minute has been created successfully.",
          });
          onSuccess();
          onCancel();
        },
        onError: (error) => {
          console.error("Error creating procurement minute:", error);
          notification.error({
            message: "Error Creating Minute",
            description: error?.response?.errors?.[0]?.message || "Failed to create procurement minute. Please try again.",
          });
        },
      });
    } catch (error) {
      console.error("Error creating procurement minute:", error);
      notification.error({
        message: "Error Creating Minute",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Add committee member
  const addCommitteeMember = () => {
    const newMember = {
      fullName: "",
      role: "MEMBER",
      organization: "",
      linkedUserId: "",
    };
    const updatedMembers = [...committeeMembers, newMember];
    setCommitteeMembers(updatedMembers);
    form.setFieldValue("committeeMembers", updatedMembers);
  };

  // Remove committee member
  const removeCommitteeMember = (index: number) => {
    const updatedMembers = [...committeeMembers];
    updatedMembers.splice(index, 1);
    setCommitteeMembers(updatedMembers);
    form.setFieldValue("committeeMembers", updatedMembers);
  };

  // Committee members table columns
  const committeeColumns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (text: string, record: any, index: number) => (
        <Form.Item
          name={["committeeMembers", index, "fullName"]}
          rules={[{ required: true, message: "Please enter full name" }]}
          style={{ margin: 0 }}
        >
          <Input placeholder="Enter full name" />
        </Form.Item>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text: string, record: any, index: number) => (
        <Form.Item
          name={["committeeMembers", index, "role"]}
          rules={[{ required: true, message: "Please select role" }]}
          style={{ margin: 0 }}
        >
          <Select placeholder="Select role">
            <Option value="CHAIR">Chair</Option>
            <Option value="MEMBER">Member</Option>
            <Option value="SECRETARY">Secretary</Option>
            <Option value="OBSERVER">Observer</Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Organization",
      dataIndex: "organization",
      key: "organization",
      render: (text: string, record: any, index: number) => (
        <Form.Item
          name={["committeeMembers", index, "organization"]}
          style={{ margin: 0 }}
        >
          <Input placeholder="Enter organization" />
        </Form.Item>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text: string, record: any, index: number) => (
        <Popconfirm
          title="Are you sure you want to delete this member?"
          onConfirm={() => removeCommitteeMember(index)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" danger icon={<MinusCircleOutlined />}>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title="Create Procurement Minute"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          committeeMembers,
          minuteTitle: sourcingTitle,
          bidOpenDate: openedAt ? dayjs(openedAt) : undefined,
          bidOpenTime: openedAt ? dayjs(openedAt) : undefined,
          meetingStart: openedAt ? dayjs(openedAt) : undefined,
          meetingEnd: openedAt ? dayjs(openedAt).add(2, 'hour') : undefined,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Minute Type"
              name="minuteType"
              rules={[{ required: true, message: "Please select minute type" }]}
            >
              <Select
                placeholder="Select minute type"
                onChange={setMinuteType}
                loading={committeeLoading || templateLoading}
              >
                <Option value="BID_OPENING">Bid Opening</Option>
                <Option value="EVALUATION_AWARD">Evaluation and Award</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Minute Title"
              name="minuteTitle"
              rules={[{ required: true, message: "Please enter minute title" }]}
            >
              <Input placeholder="Enter minute title" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Template"
          name="templateId"
          rules={[{ required: true, message: "Please select template" }]}
        >
          <Select
            placeholder="Select template"
            loading={templateLoading}
            onChange={handleTemplateChange}
          >
            {templateData?.data?.minuteTemplates?.nodes?.map((template: any) => (
              <Option key={template.id} value={template.id}>
                {template.name} (v{template.version})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Card title="Meeting Information" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Meeting Start"
                name="meetingStart"
                rules={[{ required: true, message: "Please select meeting start" }]}
              >
                <DatePicker showTime placeholder="Select start date and time" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Meeting End"
                name="meetingEnd"
                rules={[{ required: true, message: "Please select meeting end" }]}
              >
                <DatePicker showTime placeholder="Select end date and time" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Venue"
                name="venue"
                rules={[{ required: true, message: "Please enter venue" }]}
              >
                <Input placeholder="Enter venue" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Committee Members" style={{ marginBottom: 16 }}>
          <Table
            dataSource={committeeMembers}
            columns={committeeColumns}
            rowKey={(record, index) => index?.toString() || record?.fullName}
            pagination={false}
            bordered
            size="small"
          />
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addCommitteeMember}
            style={{ marginTop: 16 }}
            block
          >
            Add Committee Member
          </Button>
        </Card>

        {minuteType === "BID_OPENING" && (
          <Card title="Bid Opening Information" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Bid Open Date"
                  name="bidOpenDate"
                  rules={[{ required: true, message: "Please select bid open date" }]}
                >
                  <DatePicker placeholder="Select bid open date" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Bid Open Time"
                  name="bidOpenTime"
                  rules={[{ required: true, message: "Please select bid open time" }]}
                >
                  <TimePicker placeholder="Select bid open time" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        <Card title="Auto-Populated Information" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <p><strong>Sourcing Title:</strong> {sourcingTitle}</p>
            <p><strong>Sourcing Type:</strong> {sourcingType}</p>
            <p><strong>Invited Suppliers:</strong> {invitedSuppliers.length}</p>
            <p><strong>Published Date:</strong> {publishedDate ? new Date(publishedDate).toLocaleDateString() : 'N/A'}</p>
            <p><strong>Submitted Bids:</strong> {submittedBids.length}</p>
          </Space>
        </Card>

        <Form.Item style={{ textAlign: "right", marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={<FileTextOutlined />} loading={isLoading}>
              Create Minute
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};